import { queryOptions } from '@tanstack/react-query';
import ApiMethods from 'shared/api/api-methods';
import type { StatusResponse } from 'shared/api/services/types';
import type { Backup, CreateBackup, RestoreBackup } from 'shared/types/backups';
import { postgresqlQueryKeys } from '@/enteties/postgresql/api/postgresql.query-keys';
import type {
  CreatePostgresProject,
  IToggleScheduledBackups,
  PostgreDetails,
  PostgresBackup,
} from '@/enteties/postgresql/model';
import { mutationProjectKeys } from '@/enteties/project/api/projects.query-key';
import { handleMutateApiError } from 'shared/api/utils';

const url = '/postgresql';

export const cnpgScale = {
  scaleProject: async (
    slug: string,
    instances: number
  ): Promise<StatusResponse> => {
    const response = await ApiMethods.post(`${url}/${slug}/scale`, {
      instances,
    });
    if (!response.ok) throw Error(`${response.status}`);
    return await response.json();
  },
  getScaleCnpgMutationOptions: ({ slug }: { slug: string }) => ({
    mutationKey: mutationProjectKeys.scalingProject(slug),
    mutationFn: (instances: number) => cnpgScale.scaleProject(slug, instances),
  }),
};

export const createPostgresqlProject = async (
  data: CreatePostgresProject
): Promise<StatusResponse> => {
  const response = await ApiMethods.post(`${url}`, data);
  if (!response.ok) {
    return handleMutateApiError(response);
  }
  return await response.json();
};

export async function removePostgresqlProject(
  slug: string
): Promise<StatusResponse> {
  const response = await ApiMethods.delete(`${url}/${slug}`);
  if (!response.ok) throw Error(`${response.status}`);
  return await response.json();
}

export const postgresDetails = {
  getPostgresDetails: async (slug: string): Promise<PostgreDetails> => {
    const response = await ApiMethods.get(`${url}/${slug}/details`);
    if (!response.ok) throw Error(`${response.status}`);
    return await response.json();
  },
  getPostgresDetailsQueryOptions: ({ slug }: { slug: string }) =>
    queryOptions({
      queryKey: postgresqlQueryKeys.cnpgDetails(slug),
      queryFn: () => postgresDetails.getPostgresDetails(slug),
    }),
};

// BACKUPS

export const postgresBackups = {
  getPostgresBackups: async (slug: string): Promise<PostgresBackup[]> => {
    const response = await ApiMethods.get(`${url}/backup/${slug}`);
    if (!response.ok) throw Error(`${response.status}`);
    return await response.json();
  },
  getPostgresBackupsQueryOptions: ({ slug }: { slug: string }) =>
    queryOptions({
      queryKey: postgresqlQueryKeys.cnpgBackups(slug),
      queryFn: () => postgresBackups.getPostgresBackups(slug),
    }),
};

export async function deletePostgresBackup(
  slug: string,
  backupName: string
): Promise<StatusResponse> {
  const response = await ApiMethods.delete(
    `${url}/backup/${slug}/${backupName}`
  );
  if (!response.ok) throw Error(`${response.status}`);
  return await response.json();
}

export async function restorePostgresBackup(
  data: RestoreBackup
): Promise<{ serviceSlug: string }> {
  const response = await ApiMethods.post(`${url}/restore`, data);
  if (!response.ok) throw Error(`${response.status}`);
  return await response.json();
}

export async function createPostgresBackup(
  data: CreateBackup
): Promise<Backup> {
  const response = await ApiMethods.post(`${url}/backup`, data);
  if (!response.ok) throw Error(`${response.status}`);
  return await response.json();
}

export async function toggleScheduledBackups(
  data: IToggleScheduledBackups
): Promise<PostgreDetails> {
  const response = await ApiMethods.put(`${url}`, data);
  if (!response.ok) throw Error(`${response.status}`);
  return await response.json();
}
